import { useState, useEffect } from "react";
import { startOfMonth, endOfMonth } from "date-fns";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/dashboard/StatCard";
import { RevenueChart } from "@/components/dashboard/RevenueChart";
import { GSCPerformanceChart } from "@/components/dashboard/GSCPerformanceChart";
import { RecentActivity } from "@/components/dashboard/RecentActivity";
import { QuickActions } from "@/components/dashboard/QuickActions";
import { TopClients } from "@/components/dashboard/TopClients";
import { DateFilter, DateRange } from "@/components/dashboard/DateFilter";
import { OpportunitiesChart } from "@/components/dashboard/OpportunitiesChart";
import { SalesByCategory } from "@/components/dashboard/SalesByCategory";
import { TopProducts } from "@/components/dashboard/TopProducts";
import { LowStockAlert } from "@/components/dashboard/LowStockAlert";
import { ProductOfMonth } from "@/components/dashboard/ProductOfMonth";
import { UpcomingAppointments } from "@/components/dashboard/UpcomingAppointments";
import { UpcomingTasks } from "@/components/dashboard/UpcomingTasks";
import { useDashboardKPIs } from "@/hooks/useDashboardKPIs";
import { useGSCPerformance } from "@/hooks/useGSCPerformance";
import { useSettings } from "@/hooks/useSettings";
import { formatEuro } from "@/lib/utils";
import { CompanyLocationMap } from "@/components/dashboard/CompanyLocationMap";

import {
  Users,
  Euro,
  ShoppingCart,
  TrendingUp,
  MousePointer2,
  Eye,
  Percent,
  BarChart3,
  RefreshCw,
  MapPin,
  Wallet,
  Store,
  Wrench,
} from "lucide-react";

/**
 * NOTE:
 * The Google Maps JavaScript API is loaded dynamically using the API key
 * defined in the environment variable `VITE_GOOGLE_MAPS_BROWSER_KEY`.
 *
 * To make the map work on the production domain (e.g., scooterp.com) you must:
 *   1. Create a Google Cloud project and enable the "Maps JavaScript API".
 *   2. Generate an API key.
 *   3. In the Google Cloud console, restrict the key to the HTTP referrers
 *      that match your domain, e.g.:
 *        https://scooterp.com/*
 *        https://www.scooterp.com/*
 *   4. Add the key to your Vite environment variables as
 *      `VITE_GOOGLE_MAPS_BROWSER_KEY`.
 */

function formatChange(value: number, suffix: string = " vs mes anterior"): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%${suffix}`;
}

function formatNumber(value: number): string {
  return value.toLocaleString("es-ES");
}

const PERIOD_LABEL: Record<string, string> = {
  "this-month": "del mes", "last-month": "del mes", "this-quarter": "del trimestre",
  "last-quarter": "del trimestre", "this-year": "del año",
};

export default function Index() {
  const gsc = useGSCPerformance();
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });
  const [preset, setPreset] = useState("this-month");
  const kpis = useDashboardKPIs(dateRange);
  const { settings: generalSettings, loading: settingsLoading } = useSettings<"general">("general");
  const companyAddress = [
    generalSettings.address,
    generalSettings.city,
    generalSettings.province,
    generalSettings.postalCode,
    generalSettings.country,
  ].filter(Boolean).join(", ");
  const hasCompanyAddress = companyAddress.trim().length > 0 && generalSettings.address !== "Calle Principal 123";
  const period = PERIOD_LABEL[preset] || "del periodo";
  const vs = " vs periodo anterior";
  const ct = (v: number, inverse = false) => ((inverse ? v <= 0 : v >= 0) ? "increase" as const : "decrease" as const);

  const stats = [
    { title: `Ingresos ${period}`, value: kpis.loading ? null : formatEuro(kpis.revenue, false), change: formatChange(kpis.revenueChange, vs), changeType: ct(kpis.revenueChange), icon: Euro, iconColor: "text-success" },
    { title: `Gastos ${period}`, value: kpis.loading ? null : formatEuro(kpis.expenses, false), change: formatChange(kpis.expensesChange, vs), changeType: ct(kpis.expensesChange, true), icon: Wallet, iconColor: "text-destructive" },
    { title: `Ventas TPV ${period}`, value: kpis.loading ? null : formatEuro(kpis.posRevenue, false), change: formatChange(kpis.posChange, vs), changeType: ct(kpis.posChange), icon: Store, iconColor: "text-primary" },
    { title: `Reparaciones ${period}`, value: kpis.loading ? null : formatEuro(kpis.workshopRevenue, false), change: formatChange(kpis.workshopChange, vs), changeType: ct(kpis.workshopChange), icon: Wrench, iconColor: "text-accent" },
    { title: "Clientes Activos", value: kpis.loading ? null : formatNumber(kpis.totalClients), change: formatChange(kpis.clientsChange, " altas vs periodo anterior"), changeType: ct(kpis.clientsChange), icon: Users, iconColor: "text-primary" },
    { title: "Órdenes de taller activas", value: kpis.loading ? null : formatNumber(kpis.activeWorkOrders), change: "Pendiente, en proceso y esperando repuestos", changeType: "increase" as const, icon: ShoppingCart, iconColor: "text-warning" },
    { title: "Conversión TPV", value: kpis.loading ? null : `${kpis.conversionRate.toFixed(1)}%`, change: formatChange(kpis.conversionChange, " pts vs periodo anterior"), changeType: ct(kpis.conversionChange), icon: TrendingUp, iconColor: "text-accent" },
    { title: `Balance ${period}`, value: kpis.loading ? null : formatEuro(kpis.revenue - kpis.expenses, false), change: "Ingresos − gastos", changeType: ct(kpis.revenue - kpis.expenses), icon: BarChart3, iconColor: "text-success" },
  ];

  const gscStats = [
    {
      title: "Clicks (12m)",
      value: gsc.loading ? null : formatNumber(gsc.clicks),
      change: formatChange(gsc.clicksChange),
      changeType: gsc.clicksChange >= 0 ? "increase" as const : "decrease" as const,
      icon: MousePointer2,
      iconColor: "text-blue-500",
    },
    {
      title: "Impresiones (12m)",
      value: gsc.loading ? null : formatNumber(gsc.impressions),
      change: formatChange(gsc.impressionsChange),
      changeType: gsc.impressionsChange >= 0 ? "increase" as const : "decrease" as const,
      icon: Eye,
      iconColor: "text-purple-500",
    },
    {
      title: "CTR Medio (12m)",
      value: gsc.loading ? null : `${(gsc.ctr * 100).toFixed(2)}%`,
      change: formatChange(gsc.ctrChange * 100, " pts"),
      changeType: gsc.ctrChange >= 0 ? "increase" as const : "decrease" as const,
      icon: Percent,
      iconColor: "text-orange-500",
    },
    {
      title: "Posición Media (12m)",
      value: gsc.loading ? null : gsc.position.toFixed(1),
      change: `${gsc.positionChange >= 0 ? "+" : ""}${gsc.positionChange.toFixed(1)} pts`,
      changeType: gsc.positionChange <= 0 ? "increase" as const : "decrease" as const, // For position, lower is better
      icon: BarChart3,
      iconColor: "text-green-500",
    },
  ];


  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6 animate-fade-in">
        <PageHeader title="Dashboard" description="Bienvenido de vuelta. Aquí tienes un resumen de tu negocio.">
          <DateFilter dateRange={dateRange} onDateRangeChange={setDateRange} onPresetChange={setPreset} />
        </PageHeader>

        {/* Stats Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <StatCard
              key={stat.title}
              {...stat}
              value={stat.value || "0"}
              delay={index * 50}
              loading={stat.value === null}
            />
          ))}
        </div>

        {/* GSC Stats Grid (conditional) */}
        {gsc.hasData && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 px-1">
              <h2 className="text-xl font-semibold tracking-tight">Google Search Console (Últimos 12 meses)</h2>
              {gsc.lastSync && (
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  <RefreshCw className="h-3 w-3" />
                  Última sincronización: {new Date(gsc.lastSync).toLocaleString()}
                </p>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {gscStats.map((stat, index) => (
                <StatCard
                  key={stat.title}
                  {...stat}
                  value={stat.value || "0"}
                  delay={index * 50}
                  loading={stat.value === null}
                />
              ))}
            </div>
          </div>
        )}

        {/* Revenue Chart and Quick Actions */}
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <RevenueChart />
          </div>
          <div>
            <QuickActions />
          </div>
        </div>

        {/* GSC Performance Chart (conditional) */}
        {gsc.hasData && (
          <div className="grid gap-6">
            <GSCPerformanceChart />
          </div>
        )}

        {/* Appointments and Tasks Row */}
        <div className="grid gap-6 lg:grid-cols-2">
          <UpcomingAppointments />
          <UpcomingTasks />
        </div>

        {/* Sales Analytics Row */}
        <div className="grid gap-6 lg:grid-cols-3">
          <SalesByCategory dateRange={dateRange} />
          <OpportunitiesChart dateRange={dateRange} />
          <ProductOfMonth dateRange={dateRange} />
        </div>

        {/* Products Row */}
        <div className="grid gap-6 lg:grid-cols-2">
          <TopProducts dateRange={dateRange} />
          <LowStockAlert />
        </div>

        {/* Activity and Clients Row */}
        <div className="grid gap-6 lg:grid-cols-2">
          <RecentActivity />
          <TopClients />
        </div>

        {/* Location Map */}
        <section className="rounded-2xl border bg-card p-4 sm:p-6" aria-label="Mapa de ubicación">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
            <h2 className="text-lg font-semibold tracking-tight">Ubicación</h2>
          </div>
          <div className="w-full h-48 sm:h-64 rounded-lg overflow-hidden">
            {settingsLoading ? (
              <div className="w-full h-full animate-pulse bg-muted" />
            ) : hasCompanyAddress ? (
              <CompanyLocationMap address={companyAddress} title={generalSettings.companyName || "la empresa"} />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-muted text-muted-foreground text-sm p-4 text-center">
                <MapPin className="h-6 w-6" aria-hidden="true" />
                <span>No hay una dirección de empresa guardada.</span>
              </div>
            )}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
